import posthog from "posthog-js"
import { posthogEnabled } from "~/components/providers/Posthog"
import { useContext } from "react"
import { TutorialContext } from "./context"
import { CheckIcon, XIcon } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { OrbitLogo } from "~/components/icons/logos"
import { Button } from "~/components/ui/button"
import { AspectRatio } from "~/components/ui/aspect-ratio"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from "~/components/ui/dialog"
import { useOrganization } from "~/hooks/useOrganization"
import { tutorials } from "~/lib/tutorials"
import { api } from "~/trpc/react"

const tutorial = tutorials["onboarding-v1"]

export function OnboardingTour({
	onClose,
	completed = false
}: {
	onClose: () => void
	completed?: boolean
}) {
	const { organization } = useOrganization()
	const coordinator = useContext(TutorialContext)
	const navigate = useNavigate()
	const utils = api.useUtils()
	const finish = api.user.finishTutorial.useMutation({
		onSuccess: () => {
			void utils.user.getTutorialPreferences.invalidate()
		},
		onError: () =>
			toast.error("Couldn't save tutorial progress. Please try again.")
	})
	const dismiss = async () => {
		if (finish.isPending) return
		if (completed) {
			onClose()
			return
		}
		try {
			await finish.mutateAsync({
				tutorialId: "onboarding-v1",
				outcome: "dismissed"
			})
			if (posthogEnabled)
				posthog.capture("tutorial_dismissed", {
					tutorial_id: "onboarding-v1",
					phase: "welcome",
					reason: "invitation_dismissed",
					source: coordinator.pending?.source
				})
			onClose()
		} catch {
			/* Keep open for retry. */
		}
	}
	return (
		<Dialog
			open
			onOpenChange={(open) => {
				if (!open) void dismiss()
			}}
		>
			<DialogContent
				size="md"
				layout="scroll"
				data-onboarding
				onInteractOutside={(event) => event.preventDefault()}
			>
				<DialogHeader variant="divided">
					<div className="flex items-center justify-between gap-3">
						<div className="flex items-center gap-2 font-semibold">
							<OrbitLogo width={18} height={21} />
							<span>Orbit</span>
						</div>
						<Button
							variant="ghost"
							size="icon-xs"
							aria-label="Close onboarding"
							disabled={finish.isPending}
							onClick={() => void dismiss()}
						>
							<XIcon />
						</Button>
					</div>
				</DialogHeader>
				<div className="min-h-0 overflow-y-auto px-4 py-2 sm:px-7">
					{completed ? (
						<div className="flex flex-col gap-5 py-4">
							<CheckIcon className="size-8 text-orbit-text" />
							<DialogTitle>{tutorial.finishTitle}</DialogTitle>
							<DialogDescription>
								{tutorial.finishDescription}
							</DialogDescription>
							<ol className="list-decimal space-y-3 pl-5 text-sm text-text-sub">
								{tutorial.nextSteps.map((item) => (
									<li key={item}>{item}</li>
								))}
							</ol>
							<p className="text-sm text-text-small">{tutorial.replayNote}</p>
						</div>
					) : (
						<div className="flex flex-col gap-6 py-4">
							<div className="flex flex-col gap-3">
								<DialogTitle>{tutorial.title}</DialogTitle>
								<DialogDescription>{tutorial.subtitle}</DialogDescription>
							</div>
							<AspectRatio
								ratio={tutorial.headerImage.width / tutorial.headerImage.height}
							>
								<img
									src={tutorial.headerImage.src}
									alt={tutorial.headerImage.alt}
									width={tutorial.headerImage.width}
									height={tutorial.headerImage.height}
									decoding="async"
									className="h-full w-full rounded-lg object-contain"
								/>
							</AspectRatio>
							<p className="text-sm text-text-sub">{tutorial.introduction}</p>
							<p className="text-sm text-text-small">{tutorial.practiceNote}</p>
						</div>
					)}
				</div>
				<DialogFooter variant="divided">
					{completed ? (
						<>
							<Button
								variant="link"
								onClick={() => {
									onClose()
									if (organization)
										navigate(`/${organization.slug}/me/settings#tutorials`)
								}}
							>
								Tutorials settings
							</Button>
							<Button onClick={onClose}>{tutorial.finishLabel}</Button>
						</>
					) : (
						<>
							<Button
								variant="ghost"
								disabled={finish.isPending}
								onClick={() => void dismiss()}
							>
								{tutorial.dismissLabel}
							</Button>
							<Button
								disabled={!organization || finish.isPending}
								onClick={() => {
									if (!organization || !coordinator.beginOnboarding()) return
									onClose()
									navigate(`/${organization.slug}/me/onboarding`)
								}}
							>
								{tutorial.startLabel}
							</Button>
						</>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
